import type { ConsumablesMatrixData, ProductDetailsSticksData } from "@jti/fe-common";
import { Typography } from "@jti/ui/components/Typography";

import { VariantSelector } from "../variant-selector";

import { type ColorVariant } from "./ProductPerks.types";
import { getAlternativeVariantsColors, getSticksMatrixColorVariants } from "./utils";

import styles from "./ProductPerks.module.scss";

type ProductPerksProps = {
  productSku: string;
  configurableProductVariants: ColorVariant[];
  sticksData?: ProductDetailsSticksData;
  consumablesMatrixData?: ConsumablesMatrixData;
  dataTestId?: string;
};

const MAX_NUMBER_OF_COLOR_VARIANTS_TO_SHOW = 3;

export const ProductPerks = ({
  configurableProductVariants,
  sticksData,
  consumablesMatrixData,
  productSku,
  dataTestId,
}: ProductPerksProps) => {
  const colorVariants: ColorVariant[] = (() => {
    if (configurableProductVariants.length) {
      return configurableProductVariants;
    }

    const consumablesItem = consumablesMatrixData?.items?.find((x) => x.sku === productSku);

    // this is a temporary check to know which data structure to use for retrieving colors
    if (consumablesMatrixData && consumablesItem) {
      return getAlternativeVariantsColors(consumablesMatrixData, consumablesItem);
    }

    // this should be removed when sticksData is retired completely
    return sticksData ? getSticksMatrixColorVariants(sticksData, productSku) : [];
  })();

  return colorVariants.length > 0 ? (
    <div className={styles.container} data-testid={dataTestId}>
      {colorVariants.slice(0, 3).map((variant) => (
        <div className={styles.variantWrapper} key={variant.value}>
          <VariantSelector color={variant.value} label={variant.label} />
        </div>
      ))}

      {colorVariants.length > MAX_NUMBER_OF_COLOR_VARIANTS_TO_SHOW && (
        <div className={styles.variantWrapper}>
          <Typography variant="body2" as="span" weight="light" className={styles.variantMoreText}>
            +{colorVariants.length - MAX_NUMBER_OF_COLOR_VARIANTS_TO_SHOW}
          </Typography>
        </div>
      )}
    </div>
  ) : null;
};
